import React, { useState, useEffect } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { X } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchEvents } from '../services/api';

const localizer = momentLocalizer(moment);

// Explicit color assignments for known tenants. Each color is visually distinct.
const TENANT_COLORS = {
  barnone:    '#e53935', // red
  blackstar:  '#c2185b', // pink-red
  dd:         '#00838f', // dark cyan
  equify:     '#2e7d32', // dark green
  equipmatch: '#6a1b9a', // deep purple
  firstalert: '#1565c0', // dark blue
  ironbound:  '#4e342e', // brown
  jandj:      '#558b2f', // olive green
  machinery:  '#6a1b9a', // purple — kept as visible in screenshot
  mdavis:     '#00695c', // teal (was colliding with tac orange)
  mgbryan:    '#283593', // indigo
  permian:    '#388e3c', // green
  platinum:   '#7b1fa2', // purple
  prod:       '#37474f', // blue-grey
  sac:        '#9e9d24', // yellow-green
  tac:        '#e65100', // deep orange
  training:   '#0277bd', // light blue
};

// Overflow palette for tenants not in the map above — colors are chosen to be
// distinct from every color already assigned in TENANT_COLORS.
const OVERFLOW_PALETTE = [
  '#b71c1c', '#01579b', '#33691e', '#4a148c', '#e65100',
  '#006064', '#880e4f', '#1a237e', '#f57f17', '#bf360c',
];

const _assignedOverflowColors = {};

const getTenantColor = (tenantName) => {
  if (!tenantName) return '#757575';
  const key = tenantName.toLowerCase();
  if (TENANT_COLORS[key]) return TENANT_COLORS[key];

  // For unknown tenants: pick the first overflow color not already taken
  if (_assignedOverflowColors[key]) return _assignedOverflowColors[key];
  const usedColors = new Set([
    ...Object.values(TENANT_COLORS),
    ...Object.values(_assignedOverflowColors),
  ]);
  const available = OVERFLOW_PALETTE.find(c => !usedColors.has(c)) || '#757575';
  _assignedOverflowColors[key] = available;
  return available;
};

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantNames, setTenantNames] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    console.log('[Calendar] Component mounted, initializing...');
    loadEvents();
  }, []);

  const loadEvents = async () => {
    const loadId = `load_${Date.now()}`;
    try {
      console.log(`[Calendar][${loadId}] Starting to load events...`);
      setLoading(true);
      setError(null);
      
      // Calculate date range: 1 month ago to 12 months ahead
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(1); // Start of the month
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 12);
      endDate.setDate(0); // Last day of the month
      endDate.setHours(23, 59, 59, 999);
      
      console.log(`[Calendar][${loadId}] Date range calculated:`, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        rangeMonths: 13
      });
      
      console.log(`[Calendar][${loadId}] Calling fetchEvents API...`);
      const startTime = performance.now();
      const data = await fetchEvents(startDate, endDate);
      const endTime = performance.now();
      const duration = (endTime - startTime).toFixed(2);
      
      console.log(`[Calendar][${loadId}] API call completed in ${duration}ms`);
      console.log(`[Calendar][${loadId}] Received data:`, {
        isArray: Array.isArray(data),
        length: Array.isArray(data) ? data.length : 'N/A',
        dataType: typeof data,
        sample: Array.isArray(data) && data.length > 0 ? data[0] : null
      });
      
      if (!Array.isArray(data)) {
        console.warn(`[Calendar][${loadId}] Warning: API returned non-array data:`, data);
        throw new Error('API returned invalid data format. Expected an array.');
      }
      
      // Transform API data to calendar format
      console.log(`[Calendar][${loadId}] Transforming data to calendar format...`);
      const calendarEvents = data.map((event, index) => {
        try {
          const calendarEvent = {
            id: event.id,
            title: `${event.name || 'Untitled Event'}`,
            start: event.eventDateTime ? new Date(event.eventDateTime) : new Date(),
            end: event.eventDateTime ? new Date(new Date(event.eventDateTime).getTime() + 60 * 60 * 1000) : new Date(),
            resource: {
              eventId: event.id,
              tenantName: event.tenantName,
              description: event.eventDescription,
              isActive: event.isActive,
              live: event.live,
              registrationCount: event.registrationCount ?? 0,
              inventoryCount: event.inventoryCount ?? 0,
              eventTypeName: event.eventTypeName,
              eventStatusName: event.eventStatusName,
              createdDateTime: event.createdDateTime,
            },
          };
          return calendarEvent;
        } catch (transformError) {
          console.warn(`[Calendar][${loadId}] Error transforming event at index ${index}:`, {
            event,
            error: transformError.message
          });
          return null;
        }
      }).filter(event => event !== null);
      
      console.log(`[Calendar][${loadId}] Transformation complete:`, {
        originalCount: data.length,
        transformedCount: calendarEvents.length,
        filteredOut: data.length - calendarEvents.length
      });
      
      // Extract unique tenant names for the legend
      const uniqueTenants = Array.from(new Set(data.map(event => event.tenantName).filter(Boolean)));
      console.log(`[Calendar][${loadId}] Unique tenants found:`, uniqueTenants);
      
      setTenantNames(uniqueTenants);
      setEvents(calendarEvents);
      
      console.log(`[Calendar][${loadId}] Events loaded successfully:`, {
        totalEvents: calendarEvents.length,
        tenants: uniqueTenants.length,
        dateRange: {
          earliest: calendarEvents.length > 0 ? calendarEvents[0].start : null,
          latest: calendarEvents.length > 0 ? calendarEvents[calendarEvents.length - 1].start : null
        }
      });
    } catch (err) {
      const errorMessage = err.message || 'Failed to load events. Please check if the backend is running.';
      console.error(`[Calendar][${loadId}] Error loading events:`, {
        name: err.name,
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        details: err.details,
        stack: err.stack
      });
      
      // Provide more specific error messages
      let userFriendlyError = errorMessage;
      if (err.status === 500) {
        userFriendlyError = 'Server error occurred. Please check backend logs.';
      } else if (err.status === 404) {
        userFriendlyError = 'API endpoint not found. Please verify backend is deployed correctly.';
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        userFriendlyError = 'Network error. Please check your connection and API URL.';
      }
      
      setError(userFriendlyError);
    } finally {
      setLoading(false);
      console.log(`[Calendar][${loadId}] Loading state set to false`);
    }
  };

  const eventStyleGetter = (event) => {
    const tenantName = event.resource?.tenantName || 'Unknown';
    const backgroundColor = getTenantColor(tenantName);
    const style = {
      backgroundColor: backgroundColor,
      borderRadius: '5px',
      opacity: event.resource?.isActive ? 1 : 0.7,
      color: 'white',
      border: '0px',
      display: 'block',
    };
    return {
      style: style,
    };
  };

  const handleNavigate = (newDate) => {
    console.log('[Calendar] Navigation changed:', {
      from: currentDate.toISOString(),
      to: newDate.toISOString()
    });
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    console.log('[Calendar] View changed:', {
      from: currentView,
      to: newView
    });
    setCurrentView(newView);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (currentView === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleSelectEvent = (event) => {
    if (event?.isBackgroundEvent) return;
    setSelectedEvent(event);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (currentView === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-500 dark:text-slate-400 text-lg">
        Loading events...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-600 dark:text-gray-400 p-8 text-center">
        <p className="mb-4">{error}</p>
        <button
          onClick={loadEvents}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto flex flex-col h-[calc(100vh-8rem)] min-h-[500px]">
      <div className="mb-4 shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400">Tenants:</h3>
          {[...tenantNames].sort().map((tenant) => (
            <span key={tenant} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span
                className="w-5 h-5 rounded"
                style={{ backgroundColor: getTenantColor(tenant) }}
              />
              {tenant}
            </span>
          ))}
        </div>
      </div>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden p-4 flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-[400px]">
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          date={currentDate}
          view={currentView}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          onSelectEvent={handleSelectEvent}
          style={{ height: '100%' }}
          eventPropGetter={eventStyleGetter}
          tooltipAccessor={(event) => {
            const r = event.resource;
            const lines = [
              event.title,
              r?.eventId != null ? `Event ID: ${r.eventId}` : '',
              r?.description || '',
              `Tenant: ${r?.tenantName || ''}`,
              `Registrations: ${r?.registrationCount ?? 0} | Items: ${r?.inventoryCount ?? 0}`,
            ];
            return lines.filter(Boolean).join('\n');
          }}
          popup
          popupOffset={{ x: 10, y: 10 }}
          formats={{
            monthHeaderFormat: 'MMMM YYYY',
            dayHeaderFormat: 'dddd, MMMM Do',
            dayRangeHeaderFormat: ({ start, end }) =>
              `${moment(start).format('MMM Do')} - ${moment(end).format('MMM Do, YYYY')}`,
          }}
        />
        </div>
      </div>

      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
          onClick={() => setSelectedEvent(null)}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-xl shadow-xl min-w-[400px] max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start p-5 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 pr-3">{selectedEvent.title}</h3>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                aria-label="Close"
                className="p-1 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300 rounded"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Event ID</span>
                <span className="text-sm text-gray-800 dark:text-gray-200 font-mono">{selectedEvent.resource?.eventId ?? selectedEvent.id ?? '—'}</span>
              </div>
              {selectedEvent.resource?.description && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Description</span>
                  <span className="text-sm text-gray-800 dark:text-gray-200">{selectedEvent.resource.description}</span>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Tenant</span>
                <span className="text-sm text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded inline-block"
                    style={{ backgroundColor: getTenantColor(selectedEvent.resource?.tenantName) }}
                  />
                  {selectedEvent.resource?.tenantName || '—'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Date & Time</span>
                <span className="text-sm text-gray-800 dark:text-gray-200">
                  {selectedEvent.start ? moment(selectedEvent.start).format('dddd, MMMM D, YYYY [at] h:mm A') : '—'}
                </span>
              </div>
              {selectedEvent.resource?.eventTypeName && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Event Type</span>
                  <span className="text-sm text-gray-800 dark:text-gray-200">{selectedEvent.resource.eventTypeName}</span>
                </div>
              )}
              {selectedEvent.resource?.eventStatusName && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Event Status</span>
                  <span className="text-sm text-gray-800 dark:text-gray-200">{selectedEvent.resource.eventStatusName}</span>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Status</span>
                <span className="flex gap-2 flex-wrap">
                  <span
                    className={`inline-block px-2.5 py-1 rounded text-xs font-semibold ${
                      selectedEvent.resource?.isActive ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {selectedEvent.resource?.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span
                    className={`inline-block px-2.5 py-1 rounded text-xs font-semibold ${
                      selectedEvent.resource?.live ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' : 'bg-gray-50 dark:bg-slate-950 text-gray-500 dark:text-slate-400'
                    }`}
                  >
                    {selectedEvent.resource?.live ? 'Live' : 'Not Live'}
                  </span>
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Registrations</span>
                <span className="text-sm text-gray-800 dark:text-gray-200">{selectedEvent.resource?.registrationCount ?? 0}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Items (Inventory)</span>
                <span className="text-sm text-gray-800 dark:text-gray-200">{selectedEvent.resource?.inventoryCount ?? 0}</span>
              </div>
              {selectedEvent.resource?.createdDateTime && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Created</span>
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    {moment(selectedEvent.resource.createdDateTime).format('MMM D, YYYY [at] h:mm A')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;

